// Projection chart — Canvas 2D, independently maintained for the Ideation Portal.
// DPR-aware, gradient bars, rounded corners, nice Y-axis ticks.

import type { ChartConfig } from './types';

const NEG_TOP = '#5b9bd5';   // lighter near zero
const NEG_BOT = '#1a4789';   // darker at bar tip
const POS_TOP = '#4ade80';
const POS_BOT = '#16a34a';

export function createProjectionChart(config: ChartConfig): HTMLCanvasElement {
  const { values, labels, color, height } = config;
  const dpr = typeof window !== 'undefined' ? (window.devicePixelRatio || 1) : 1;
  const LOGICAL_W = 800;

  const canvas = document.createElement('canvas');
  canvas.width = LOGICAL_W * dpr;
  canvas.height = height * dpr;
  canvas.style.width = '100%';
  canvas.style.height = `${height}px`;

  const ctx = canvas.getContext('2d');
  if (!ctx || values.length === 0) return canvas;

  ctx.scale(dpr, dpr);
  _draw(ctx, values, labels, color, LOGICAL_W, height);
  return canvas;
}

export function updateProjectionChart(canvas: HTMLCanvasElement, config: ChartConfig): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const dpr = typeof window !== 'undefined' ? (window.devicePixelRatio || 1) : 1;
  const logicalW = canvas.width / dpr;
  const logicalH = canvas.height / dpr;
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.restore();
  if (config.values.length === 0) return;
  _draw(ctx, config.values, config.labels, config.color, logicalW, logicalH);
}

function _draw(
  ctx: CanvasRenderingContext2D,
  values: number[],
  labels: string[],
  _color: string,
  W: number,
  H: number,
): void {
  const pad = { top: 20, right: 14, bottom: 32, left: 54 };
  const cw = W - pad.left - pad.right;
  const ch = H - pad.top - pad.bottom;

  const rawMax = Math.max(...values, 0);
  const rawMin = Math.min(...values, 0);
  const rawRange = rawMax - rawMin;

  // No meaningful data — draw an empty chart with just the zero line.
  if (rawRange === 0) {
    const midY = pad.top + ch / 2;
    ctx.save();
    ctx.strokeStyle = '#cbd5e1';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(pad.left, midY);
    ctx.lineTo(pad.left + cw, midY);
    ctx.stroke();
    ctx.restore();
    ctx.fillStyle = '#9ca3af';
    ctx.font = '10px system-ui,-apple-system,sans-serif';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    ctx.fillText('0', pad.left - 6, midY);
    return;
  }

  // Build nice Y-axis ticks
  const ticks = _buildTicks(rawMin, rawMax, rawRange);
  const tickMin = ticks[0];
  const tickMax = ticks[ticks.length - 1];
  const tickRange = tickMax - tickMin || 1;

  const toY = (v: number) => pad.top + ((tickMax - v) / tickRange) * ch;
  const zeroY = toY(0);

  // ── White background ──────────────────────────────────────────────
  ctx.clearRect(0, 0, W, H);

  // ── Grid + Y labels ───────────────────────────────────────────────
  for (const tick of ticks) {
    const gy = toY(tick);
    if (gy < pad.top - 2 || gy > pad.top + ch + 2) continue;

    ctx.save();
    ctx.strokeStyle = '#f0f2f5';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(pad.left, gy);
    ctx.lineTo(pad.left + cw, gy);
    ctx.stroke();
    ctx.restore();

    ctx.save();
    ctx.fillStyle = '#9ca3af';
    ctx.font = '10px system-ui,-apple-system,sans-serif';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    ctx.fillText(_fmt(tick), pad.left - 6, gy);
    ctx.restore();
  }

  // ── Zero line ─────────────────────────────────────────────────────
  ctx.save();
  ctx.strokeStyle = '#cbd5e1';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(pad.left, zeroY);
  ctx.lineTo(pad.left + cw, zeroY);
  ctx.stroke();
  ctx.restore();

  // ── Bars ──────────────────────────────────────────────────────────
  const slot = cw / values.length;
  const gapRatio = values.length > 24 ? 0.14 : 0.18;
  const gap = Math.max(1, slot * gapRatio);
  const bw = slot - gap;
  const r = Math.min(3, bw / 4);

  values.forEach((v, i) => {
    const x = pad.left + i * slot + gap / 2;
    const barH = Math.abs(toY(v) - zeroY);
    if (barH < 0.5) return;

    const isPos = v >= 0;
    const barTop = isPos ? zeroY - barH : zeroY;

    const grad = ctx.createLinearGradient(x, barTop, x, barTop + barH);
    if (isPos) {
      grad.addColorStop(0, POS_TOP);
      grad.addColorStop(1, POS_BOT);
    } else {
      grad.addColorStop(0, NEG_TOP);
      grad.addColorStop(1, NEG_BOT);
    }

    ctx.save();
    ctx.fillStyle = grad;
    _roundedBar(ctx, x, barTop, bw, barH, r, isPos);
    ctx.fill();
    ctx.restore();
  });

  // ── X labels (every 3 months, horizontal) ─────────────────────────
  const labelEvery = values.length <= 18 ? 3 : 6;
  ctx.save();
  ctx.fillStyle = '#9ca3af';
  ctx.font = '10px system-ui,-apple-system,sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  labels.forEach((lbl, i) => {
    if (i % labelEvery !== 0 || !lbl) return;
    const x = pad.left + i * slot + slot / 2;
    ctx.fillText(lbl, x, pad.top + ch + 6);
  });
  ctx.restore();
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function _buildTicks(rawMin: number, rawMax: number, rawRange: number): number[] {
  let step = _niceStep(rawRange / 5);
  let ticks = _ticksForStep(rawMin, rawMax, step);
  let guard = 0;
  while (ticks.length > 8 && ++guard < 20) {
    step = _niceStep(step * 2.1);
    ticks = _ticksForStep(rawMin, rawMax, step);
  }
  guard = 0;
  while (ticks.length < 3 && step > 1e-9 && ++guard < 20) {
    step = _niceStep(step / 2);
    ticks = _ticksForStep(rawMin, rawMax, step);
  }
  return ticks.length > 0 ? ticks : [rawMin, rawMax];
}

function _ticksForStep(rawMin: number, rawMax: number, step: number): number[] {
  const niceMin = Math.floor(rawMin / step) * step;
  const niceMax = Math.ceil(rawMax / step) * step;
  const result: number[] = [];
  for (let v = niceMin; v <= niceMax + step * 0.01; v += step) {
    result.push(Math.round(v / step) * step);
  }
  return result;
}

function _niceStep(rough: number): number {
  if (rough <= 0) return 1;
  const exp = Math.floor(Math.log10(rough));
  const f = rough / Math.pow(10, exp);
  const nf = f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10;
  return nf * Math.pow(10, exp);
}

function _fmt(v: number): string {
  if (v === 0) return '0';
  const abs = Math.abs(v);
  const sign = v < 0 ? '−' : '';
  if (abs >= 1_000_000) return `${sign}${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}${Math.round(abs / 1_000)}K`;
  return `${sign}${Math.round(abs)}`;
}

function _roundedBar(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number,
  roundTop: boolean,
): void {
  ctx.beginPath();
  if (roundTop) {
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.arcTo(x + w, y, x + w, y + r, r);
    ctx.lineTo(x + w, y + h);
    ctx.lineTo(x, y + h);
    ctx.lineTo(x, y + r);
    ctx.arcTo(x, y, x + r, y, r);
  } else {
    ctx.moveTo(x, y);
    ctx.lineTo(x + w, y);
    ctx.lineTo(x + w, y + h - r);
    ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
    ctx.lineTo(x + r, y + h);
    ctx.arcTo(x, y + h, x, y + h - r, r);
    ctx.lineTo(x, y);
  }
  ctx.closePath();
}
